import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskText } from "./MaskText";
import { useFilms } from "../../context/FilmsContext";
import { ArrowDown, Play } from "lucide-react";

export const Hero = ({ onEnter, onBrowse }) => {
  const { films } = useFilms();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden vignette"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/86fb1679-d749-4b31-9538-591f84a31f12/images/d363fb37567dd8e3fe7abe029c820499ac43ea8cc2fef6cfb17558c1e740ee3e.jpeg"
          alt=""
          className="h-full w-full object-cover object-center opacity-25 grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-center pt-24"
      >
        <MaskText delay={0.5} className="font-mono text-[11px] md:text-xs tracking-[0.4em] uppercase text-blood">
          Public Domain Cinema · Est. MCMII
        </MaskText>

        <h1 className="mt-6 font-serif font-black leading-[0.82] tracking-tighter text-bone text-[19vw] md:text-[15vw] lg:text-[13rem]">
          <MaskText delay={0.62}>OLD</MaskText>
          <MaskText delay={0.74} className="italic text-blood pl-[0.06em]">
            GEEZER
          </MaskText>
        </h1>

        <div className="mt-8 md:mt-10 grid md:grid-cols-[1.1fr_auto] gap-8 md:items-end">
          <div className="line-mask max-w-xl">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1], delay: 0.95 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed"
            >
              Classic horror & science-fiction, restored and streaming for
              two dollars a month. Cheaper than a coffee, darker than midnight —
              the films that built the genre, all in one vault.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-enter-btn"
              onClick={onEnter}
              className="group inline-flex items-center justify-center gap-3 bg-blood text-white px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase transition-transform duration-300 hover:-translate-y-0.5"
            >
              <Play className="h-4 w-4 fill-current" />
              Enter the Vault
            </button>
            <button
              data-testid="hero-browse-btn"
              onClick={onBrowse}
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-bone px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-bone transition-colors duration-300"
            >
              Browse the Archive
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-28 md:bottom-32 left-5 md:left-10 z-10 flex items-center gap-3 text-muted-foreground"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
          Scroll · {films.length} titles inside
        </span>
      </motion.div>
    </section>
  );
};
